import React, { useState } from 'react';
import './vehicleDetailedPopup.scss';
import { VehicleBasicInfo, VehicleDetailInfo } from '../../../types/vehicle.type';
import lcn from '../../../assets/svgs/map-pin.icon.svg';
import { LineChart } from '@mui/x-charts/LineChart';
import AiAnalysis from '../aiAnalysis/AiAnalysis';
import '../aiAnalysis/AiAnalysis.css';
import { useNavigate } from 'react-router-dom';

interface Props {
    detailInfo: VehicleDetailInfo;
    basicInfo: VehicleBasicInfo;
    onClose: () => void;
}

// Utility to map status to class
const getStatushClass = (status: string) => {
    switch (status.toLowerCase()) {
        case 'moving':
            return 'moving_status';
        case 'idle':
            return 'idle_status';
        case 'parked':
            return 'parked_status';
        case 'offline':
            return 'offline_status';
        default:
            return '';
    }
};

// Utility to map health status to class
const getConditionClass = (healthStatus: string) => {
    switch (healthStatus.toLowerCase()) {
        case 'good':
            return 'moving_status';
        case 'average':
            return 'idle_status';
        case 'critical':
            return 'offline_status';
        default:
            return '';
    }
};

// Utility to map health status to background color
const getHealthStatusBg = (healthStatus: string) => {
    const base = { color: '#fff', padding: '5px' };
    switch (healthStatus.toLowerCase()) {
        case 'good':
            return { ...base, background: '#4CAF50' };
        case 'fair':
            return { ...base, background: '#FFA000' };
        case 'critical':
            return { ...base, background: '#D32F2F' };
        case 'average':
            return { ...base, background: '#2196F3' };
        default:
            return { ...base, background: '#888' };
    }
};

// Utility to map status to class and color
const statusColorMap: Record<string, { text: string; dot: string }> = {
    moving: { text: '#4CAF50', dot: '#4CAF50' }, // green
    idle: { text: '#FFC107', dot: '#FFC107' }, // amber
    parked: { text: '#2196F3', dot: '#2196F3' }, // blue
    offline: { text: '#9E9E9E', dot: '#9E9E9E' }, // grey
    alerts: { text: '#FF3030', dot: '#FF3030' }, // red
};

const getStatusColors = (status: string) => {
    const key = status.toLowerCase();
    return statusColorMap[key] || { text: '#fff', dot: '#fff' };
};

const VehicleDetailedPopup: React.FC<Props> = ({ detailInfo, basicInfo, onClose }) => {
    const { vehicleSpecs, alerts, healthStatus, maintenanceHistory, charts } = detailInfo;
    const { imageUrl, vehicle, deviceId, licensePlate, status, location } = basicInfo;

    const [activeTab, setActiveTab] = useState<'alerts' | 'maintenance'>('alerts');
    const [engineTempDuration, setEngineTempDuration] = useState<'Year' | 'Month' | 'Week' | 'Day'>('Year');
    const [mileageDuration, setMileageDuration] = useState<'Year' | 'Month' | 'Week' | 'Day'>('Year');
    const [engineHoursDuration, setEngineHoursDuration] = useState<'Year' | 'Month' | 'Week' | 'Day'>('Year');
    const [engineLoadDuration, setEngineLoadDuration] = useState<'Year' | 'Month' | 'Week' | 'Day'>('Year');
    const [showAiModal, setShowAiModal] = useState(false);

    const navigate = useNavigate();

    // Helper to get chart data by type and duration
    const getChartData = (type: 'engineTemperature' | 'mileage' | 'engineHours' | 'engineLoad', duration: string) => {
        if (!charts || !(charts as any)[type]) return { data: [], unit: '' };
        const found = (charts as any)[type].find((c: any) => c.duration === duration);
        return found ? found : { data: [], unit: '' };
    };

    // X-axis labels for each duration, always matching the data length
    const getXAxisLabels = (duration: string, type: 'engineTemperature' | 'mileage' | 'engineHours' | 'engineLoad') => {
        const dataLen = getChartData(type, duration).data.length;
        switch (duration) {
            case 'Day': {
                const now = new Date();
                return Array.from({ length: dataLen }, (_, i) => {
                    const d = new Date(now);
                    d.setHours(now.getHours() - (dataLen - 1 - i));
                    const hh = String(d.getHours()).padStart(2, '0');
                    return `${hh}:00`;
                });
            }
            case 'Week': {
                const base = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                if (dataLen <= 7) return base.slice(0, dataLen);
                return Array.from({ length: dataLen }, (_, i) => base[i % 7]);
            }
            case 'Week': {
                const base = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                if (dataLen <= 7) return base.slice(0, dataLen);
                return Array.from({ length: dataLen }, (_, i) => base[i % 7]);
            }
            case 'Month': {
                const today = new Date();
                return Array.from({ length: dataLen }, (_, i) => {
                    const d = new Date(today);
                    d.setDate(today.getDate() - (dataLen - 1 - i));
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    return `${mm}/${dd}`;
                });
            }
            case 'Year': {
                const base = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                if (dataLen <= 12) return base.slice(0, dataLen);
                return Array.from({ length: dataLen }, (_, i) => base[i % 12]);
            }
            default:
                return Array.from({ length: dataLen }, () => '');
        }
    };

    const handleClose = () => {
        onClose();
        navigate('/dashboard');
    };

    return (
        <div className="vehicle-popup-overlay">
            <div className="vehicle-popup">
                <header >
                    <div className="vehicle" >
                        <div className="vehicle-title">
                            <span className="vehicle-name" title={vehicle} >{vehicle}</span>
                            |
                            <span className="device-id" title={deviceId}> DEVICE ID - {deviceId}</span>
                            |
                            <span className="license" title={licensePlate}> License Plate No. - {licensePlate}</span>
                        </div>
                        <div className="vehicle-status">
                            <span
                                className={`issue-type ${getStatushClass(status)}`}
                                style={{ color: getStatusColors(status).text }}
                            >
                                <span
                                    className="status-dot"
                                    style={{ backgroundColor: getStatusColors(status).dot }}
                                ></span>
                                {status}
                            </span>
                            <span className="health-status" style={{ color: '#fff' }}>
                                Health Status <span className={`issue-type ${getConditionClass(healthStatus)}`} style={getHealthStatusBg(healthStatus)}>{healthStatus}</span>
                            </span>
                            <span className="location" title={location}>
                                <img src={lcn} alt="Location" style={{ width: '16px' }} /> {location}
                            </span>
                        </div>
                        <button className="RUN AI ANALYSIS" style={{ backgroundColor: "#023E58", color: '#fff', padding: '10px 25px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', transition: 'background 0.2s' }} onClick={() => setShowAiModal(true)}>
                            RUN AI ANALYSIS
                        </button>
                    </div>
                    <button className="close-btn" onClick={handleClose}>×</button>
                </header>

                <section className="popup-content">


                    <aside className="pic-and-list">
                        <figure>
                            <img src={imageUrl || ''} alt="Vehicle" />
                        </figure>
                        <div className="tabs-section">
                            <div className="tab-header">
                                <button
                                    className={activeTab === 'alerts' ? 'active' : ''}
                                    onClick={() => setActiveTab('alerts')}
                                >
                                    Alerts
                                </button>
                                <button
                                    className={activeTab === 'maintenance' ? 'active' : ''}
                                    onClick={() => setActiveTab('maintenance')}
                                >
                                    Maintenance History
                                </button>
                            </div>
                            <div className="tab-content">
                                {activeTab === 'alerts' && (
                                    <section className="tab">
                                        {alerts.length ? (
                                            <ul className="list">
                                                {alerts.map((alert, index) => (
                                                    <li key={index} className={`severity-${alert.severity.toLowerCase()}`}>
                                                        <aside className="label-n-time">
                                                            <label>{alert.type}</label>
                                                            <time>{new Date(alert.timestamp).toLocaleString()}</time>
                                                        </aside>
                                                        <mark className={`${alert.severity.toLowerCase()}`}>{alert.severity}</mark>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : <p>No alerts.</p>}
                                    </section>
                                )}
                                {activeTab === 'maintenance' && (
                                    <section className="maintenance-timeline">
                                        {maintenanceHistory.length ? (
                                            <ul style={{ backgroundColor: '#222222', padding: '15px', borderRadius: '10px' }}>
                                                {maintenanceHistory.map((item: any, index: number) => (
                                                    <li className="timeline-item" key={index}>
                                                        <div
                                                            className="timeline-line"
                                                            style={index === maintenanceHistory.length - 1 ? { height: '10px' } : {}}
                                                        />
                                                        <div className="timeline-dot" />
                                                        <div className="timeline-content">
                                                            <div className="timeline-title">{item.type}</div>
                                                            <div className={`timeline-severity ${item.severity.toLowerCase()}`}>{item.severity}</div>
                                                            <div className="timeline-date">
                                                                Maintenance Date: {new Date(item.date).toLocaleDateString()} | {new Date(item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : <p>No maintenance records.</p>}
                                    </section>
                                )}
                            </div>
                        </div>
                    </aside>

                    <article className="selected-vehicle-info">
                        <div className="grid-container">

                            <ul className="grid-block horizontal-list">
                                {
                                    Object.entries(vehicleSpecs).map(([key, value], index) => (
                                        <li key={index}>
                                            <span>{value}</span>
                                            <label>{key}</label>
                                        </li>
                                    ))
                                }
                            </ul>
                            <div className="grid-block">
                                <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                    <h4 style={{ color: '#fff', fontSize: '16px' }}>Engine Temperature</h4>
                                    <select value={engineTempDuration} onChange={e => setEngineTempDuration(e.target.value as any)}>
                                        <option value="Year">Year</option>
                                        <option value="Month">Month</option>
                                        <option value="Week">Week</option>
                                        <option value="Day">Day</option>
                                    </select>
                                </div>
                                <LineChart
                                    xAxis={[{ data: getXAxisLabels(engineTempDuration, 'engineTemperature'), scaleType: 'band' }]}
                                    series={[
                                        {
                                            data: getChartData('engineTemperature', engineTempDuration).data,
                                            color: '#D94C7D'
                                        },
                                    ]}
                                    height={200}
                                />
                            </div>

                            <div className="grid-block">
                                <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                    <h4 style={{ color: '#fff', fontSize: '16px' }}>Mileage</h4>
                                    <select value={mileageDuration} onChange={e => setMileageDuration(e.target.value as any)}>
                                        <option value="Year">Year</option>
                                        <option value="Month">Month</option>
                                        <option value="Week">Week</option>
                                        <option value="Day">Day</option>
                                    </select>
                                </div>
                                <LineChart
                                    xAxis={[{ data: getXAxisLabels(mileageDuration, 'mileage'), scaleType: 'band' }]}
                                    series={[
                                        {
                                            data: getChartData('mileage', mileageDuration).data,
                                            color: '#2A66C5'
                                        },
                                    ]}
                                    height={200}
                                />
                            </div>
                            <div className="grid-block">
                                <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                    <h4 style={{ color: '#fff', fontSize: '16px' }}>Engine Hours</h4>
                                    <select value={engineHoursDuration} onChange={e => setEngineHoursDuration(e.target.value as any)}>
                                        <option value="Year">Year</option>
                                        <option value="Month">Month</option>
                                        <option value="Week">Week</option>
                                        <option value="Day">Day</option>
                                    </select>
                                </div>
                                <LineChart
                                    xAxis={[{ data: getXAxisLabels(engineHoursDuration, 'engineHours'), scaleType: 'band' }]}
                                    series={[
                                        {
                                            data: getChartData('engineHours', engineHoursDuration).data,
                                            color: '#F19D38'
                                        },
                                    ]}
                                    height={200}
                                />
                            </div>
                            <div className="grid-block">
                                <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                    <h4 style={{ color: '#fff', fontSize: '16px' }}>Engine % Load</h4>
                                    <select value={engineLoadDuration} onChange={e => setEngineLoadDuration(e.target.value as any)}>
                                        <option value="Year">Year</option>
                                        <option value="Month">Month</option>
                                        <option value="Week">Week</option>
                                        <option value="Day">Day</option>
                                    </select>
                                </div>
                                <LineChart
                                    xAxis={[{ data: getXAxisLabels(engineLoadDuration, 'engineLoad'), scaleType: 'band' }]}
                                    series={[
                                        {
                                            data: getChartData('engineLoad', engineLoadDuration).data,
                                            color: '#549D55'
                                        },
                                    ]}
                                    height={200}
                                />
                            </div>
                        </div>
                    </article>

                </section>
            </div>
            {showAiModal && (
                <div className="ai-modal-overlay">
                    <div className="ai-modal">
                        <button className="ai-modal-close" onClick={handleClose}>×</button>
                        <AiAnalysis reportData={detailInfo.reportData} vehicleName={vehicle} onBack={() => setShowAiModal(false)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default VehicleDetailedPopup;
